/**
 * Czysty, stabilny względem FPS profil follow kotka.
 * Dokładne rozwiązanie krytycznie tłumionej sprężyny dla stałego celu w kroku.
 */

export type CompanionFollowState = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  bobPhase: number;
  bobOffsetY: number;
};

export type CompanionFollowOptions = {
  /** Maksymalna odległość od celu po teleportach / zmianie sceny. */
  maxDistance?: number;
  /** Częstość odpowiedzi sprężyny w rad/s. */
  angularFrequency?: number;
  bobAmplitude?: number;
  bobHz?: number;
};

const DEFAULT_MAX_DISTANCE = 220;
const DEFAULT_ANGULAR_FREQUENCY = 10;
const DEFAULT_BOB_AMPLITUDE = 2.5;
const DEFAULT_BOB_HZ = 1.35;

export function createCompanionFollowState(
  x: number,
  y: number,
): CompanionFollowState {
  return {
    x: Number.isFinite(x) ? x : 0,
    y: Number.isFinite(y) ? y : 0,
    vx: 0,
    vy: 0,
    bobPhase: 0,
    bobOffsetY: 0,
  };
}

function springAxis(
  position: number,
  velocity: number,
  target: number,
  dt: number,
  omega: number,
): { position: number; velocity: number } {
  const displacement = position - target;
  const c = velocity + omega * displacement;
  const decay = Math.exp(-omega * dt);
  return {
    position: target + (displacement + c * dt) * decay,
    velocity: (velocity - omega * c * dt) * decay,
  };
}

export function stepCompanionFollow(
  state: CompanionFollowState,
  target: { x: number; y: number },
  dtSec: number,
  opts: CompanionFollowOptions = {},
): CompanionFollowState {
  const dt = Number.isFinite(dtSec) ? Math.max(0, Math.min(0.1, dtSec)) : 0;
  const tx = Number.isFinite(target.x) ? target.x : state.x;
  const ty = Number.isFinite(target.y) ? target.y : state.y;
  const maxDistance = Math.max(1, opts.maxDistance ?? DEFAULT_MAX_DISTANCE);
  const omega = Math.max(
    0.1,
    opts.angularFrequency ?? DEFAULT_ANGULAR_FREQUENCY,
  );

  let x = state.x;
  let y = state.y;
  let vx = state.vx;
  let vy = state.vy;
  const dx = tx - x;
  const dy = ty - y;
  const distance = Math.hypot(dx, dy);
  if (distance > maxDistance) {
    const inv = 1 / distance;
    x = tx - dx * inv * maxDistance;
    y = ty - dy * inv * maxDistance;
    vx = 0;
    vy = 0;
  }

  const sx = springAxis(x, vx, tx, dt, omega);
  const sy = springAxis(y, vy, ty, dt, omega);
  const bobPhase =
    (state.bobPhase +
      dt * Math.PI * 2 * Math.max(0, opts.bobHz ?? DEFAULT_BOB_HZ)) %
    (Math.PI * 2);
  const remaining = Math.hypot(tx - sx.position, ty - sy.position);
  const speed = Math.hypot(sx.velocity, sy.velocity);
  const calm = remaining < 1.5 && speed < 8;
  const bobOffsetY = calm
    ? Math.sin(bobPhase) *
      Math.max(0, opts.bobAmplitude ?? DEFAULT_BOB_AMPLITUDE)
    : 0;

  return {
    x: sx.position,
    y: sy.position,
    vx: sx.velocity,
    vy: sy.velocity,
    bobPhase,
    bobOffsetY,
  };
}
